// Todo:
// - Melhorar efeito do PlaceholderSlide
// - Colocar fadeIn nos componentes Video, Quiz, Minigame...

import React, { memo, useEffect, useState } from 'react';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import _ from 'lodash';

import styles from './CoursePlayer.module.scss';
import Header from './Header/Header';
import NavFooter from './NavFooter/NavFooter';
import Main from './Main/Main';
import Menu from './Menu/Menu';
import OutsideAlerter from './UI/OutsiderAlerter/OutsiderAlerter';
import { isViewportMobile } from './utils/utils';
import useGlobal from './hooks/useGlobal';
import useScorm from './hooks/useScorm';
import usePrevious from './hooks/usePrevious';
import Spinner from './UI/Spinner/Spinner';
import Preloader from './Preloader/Preloader';
import LabelProvider from './context/Label/LabelContext';
import { labels, config } from './constants/labels.json';
import UIProvider from './context/UI/UIContext';
import ConfirmDialog from './UI/ConfirmDialog/ConfirmDialog';
import BookmarkDialog from './BookmarkDialog/BookmarkDialog';
import LicenseProvider from './context/License/LicenseContext';
import LicenseCheck from './UI/LicenseCheck/LicenseCheck';

function CoursePlayer(props) {
  const {
    course,
    onPageChanged,
    curPage,
    disableScorm,
    editMode = false,
    customLabels,
    onLoad,
    testFunction,
    emulatedScormData = {},
  } = props;
  const globalState = useGlobal();
  const {
    player,
    scorm,
    toggleMenu,
    initCourseData,
    getCurrentPage,
    goPage,
    setScormReady,
    setScormInitialData,
    setDisableScorm,
    restoreTracking,
    handleError,
    setMessageCourseCompleted,
    setEditMode,
    setTestCallback,
  } = globalState;
  const currentPage = getCurrentPage();
  const prevCurrentPage = usePrevious(currentPage);
  const { initializeScormAsync, getSuspendData } = useScorm();
  const [isShownBookmarkDialog, setIsShownBookmarkDialog] = useState(false);

  useEffect(() => {
    // edit mode
    setEditMode(!!editMode);

    // on load
    onLoad && onLoad({ labels, languages: config.languages });

    // disable scorm coming from props
    if (disableScorm) {
      setDisableScorm(true);
      return;
    }

    doInitializeScorm();

    testFunction && setTestCallback(testFunction);

    async function doInitializeScorm() {
      try {
        // initialize SCORM
        const scormData = await initializeScormAsync({ emulatedScormData });
        console.log('Scorm initialized successfully:', scormData);

        // restore suspend_data
        // first, using suspend_data
        let suspendData = {};
        try {
          if (!_.isEmpty(scormData?.suspendData)) {
            suspendData = getSuspendData(scormData?.suspendData);
          }
        } catch (err) {
          console.error(err);
          handleError(new Error('Cannot parse suspend_data'));
          console.log({ scormData });
        }

        // second, from completed on lessonLocation
        if (!_.isEmpty(scormData?.lessonLocation)) {
          try {
            const { completed } = JSON.parse(scormData?.lessonLocation || '{}');
            const { pages = [] } = course || {};
            _.forEach(pages, (page, pageIdx) => {
              if (page?.resourceId && _.includes(completed, pageIdx)) {
                suspendData = {
                  ...suspendData,
                  [page.resourceId]: {
                    ...suspendData[page.resourceId],
                    completed: true,
                  },
                };
              }
            });
          } catch (err) {
            console.error(err);
            handleError(new Error('Cannot parse lesson_location'));
            console.log({ scormData });
          }
        }
        // finally restore
        restoreTracking(suspendData);

        setScormInitialData(scormData);
        setScormReady(true);
      } catch (err) {
        handleError(err);
        setDisableScorm(true);
      }
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    initCourseData(course);
  }, [course]); //eslint-disable-line

  useEffect(() => {
    // avoid first time calling
    if (
      !currentPage ||
      !prevCurrentPage ||
      currentPage.resourceId === prevCurrentPage.resourceId
    ) {
      if (currentPage?.facevideo?.name && !player?.showMenu) toggleMenu();
      return;
    }

    onPageChanged && onPageChanged(currentPage);
    globalState.onPageChanged(currentPage);
  }, [currentPage]); //eslint-disable-line

  useEffect(() => {
    !isNaN(curPage) && goPage(curPage);
  }, [curPage]); //eslint-disable-line

  function handleClickOutside() {
    isViewportMobile() && player?.showMenu && toggleMenu();
  }

  function renderMenu() {
    const className = [styles.CoursePlayer__menu];

    !player?.showMenu && className.push(styles['CoursePlayer__menu--hidden']);

    return (
      <OutsideAlerter onClickOutside={handleClickOutside}>
        <div className={className.join(' ')}>
          <Menu globalState={globalState} />
        </div>
      </OutsideAlerter>
    );
  }

  function handleLanguageChanged(language) {
    setMessageCourseCompleted(_.get(labels, `Course-completed.${language}`));
  }

  const canRenderCourse = !!course && (scorm.scormReady || player.disableScorm);

  if (!canRenderCourse) return <Spinner />;

  return (
    <LabelProvider
      labels={labels}
      language={course?.language}
      languages={config.languages}
      customLabels={customLabels}
      onLanguageChanged={handleLanguageChanged}
    >
      <UIProvider>
        <LicenseProvider>
          <div className={styles.CoursePlayer}>
            <ToastContainer transition={Slide} />
            {scorm.scormReady && <LicenseCheck globalState={globalState} />}
            <ConfirmDialog />
            <BookmarkDialog
              globalState={globalState}
              onDialogShow={() => setIsShownBookmarkDialog(true)}
              onDialogHide={() => setIsShownBookmarkDialog(false)}
            />
            {!editMode && (
              <Preloader
                course={course}
                curPage={globalState?.player?.curPage}
              />
            )}
            {!isShownBookmarkDialog && renderMenu()}
            {!isShownBookmarkDialog && (
              <div className={styles.CoursePlayer__main}>
                <Header globalState={globalState} />
                <Main globalState={globalState} />
                <NavFooter globalState={globalState} />
              </div>
            )}
          </div>
        </LicenseProvider>
      </UIProvider>
    </LabelProvider>
  );
}

export default memo(CoursePlayer);
